import React, { useState } from 'react';
import { X, AlertTriangle, Check, CheckSquare, Square, FileText, Music, Ban } from 'lucide-react';

interface FileMatch {
  fileName: string;
  filePath: string;
  matchConfidence: number;
  fileType?: string;
  fileSize?: number;
}

interface FileConflict {
  songId: number;
  songTitle: string;
  matches: FileMatch[];
}

interface ConflictResolution {
  songId: number;
  songTitle: string;
  selectedFiles: string[];
  action: 'all' | 'some' | 'skip';
}

interface FileConflictModalProps {
  conflicts: FileConflict[];
  onResolve: (resolutions: ConflictResolution[]) => Promise<void> | void;
  onCancel: () => void;
}

const FileConflictModal: React.FC<FileConflictModalProps> = ({ 
  conflicts, 
  onResolve, 
  onCancel 
}) => {
  const [isSending, setIsSending] = useState(false);
  const [resolutions, setResolutions] = useState<Map<number, ConflictResolution>>(
    new Map(conflicts.map(conflict => {
      // Sort matches by confidence (highest first)
      const sortedMatches = [...conflict.matches].sort((a, b) => b.matchConfidence - a.matchConfidence);

      // Auto-select highest confidence file (or first match if none have high confidence)
      const highestConfidence = sortedMatches[0];

      return [
        conflict.songId,
        {
          songId: conflict.songId,
          songTitle: conflict.songTitle,
          selectedFiles: highestConfidence ? [highestConfidence.filePath] : [],
          action: 'some' // Default to "Select Some" with highest confidence file pre-selected
        }
      ];
    }))
  );

  const updateResolution = (songId: number, updates: Partial<ConflictResolution>) => {
    const newResolutions = new Map(resolutions);
    const current = newResolutions.get(songId);
    if (current) {
      newResolutions.set(songId, { ...current, ...updates });
      setResolutions(newResolutions);
    }
  };

  const handleActionChange = (songId: number, action: 'all' | 'some' | 'skip') => {
    const conflict = conflicts.find(c => c.songId === songId);
    if (!conflict) return;

    const selectedFiles = action === 'all' 
      ? conflict.matches.map(m => m.filePath)
      : action === 'some' 
      ? []  // Will be selected manually
      : []; // Skip means no files

    updateResolution(songId, { action, selectedFiles });
  };

  const handleFileToggle = (songId: number, filePath: string) => {
    const current = resolutions.get(songId);
    if (!current || current.action !== 'some') return;

    const selectedFiles = current.selectedFiles.includes(filePath)
      ? current.selectedFiles.filter(path => path !== filePath)
      : [...current.selectedFiles, filePath];

    updateResolution(songId, { selectedFiles });
  };

  const handleSelectSuggested = (songId: number) => {
    const conflict = conflicts.find(c => c.songId === songId);
    if (!conflict) return;

    // Suggest files with highest confidence, or files with "CONG" or "BULLETIN" in name
    const suggested = conflict.matches.filter(match => 
      match.fileName.toLowerCase().includes('cong') ||
      match.fileName.toLowerCase().includes('bulletin') ||
      match.matchConfidence >= 95
    );

    const selectedFiles = suggested.length > 0 
      ? suggested.map(m => m.filePath)
      : [conflict.matches[0]?.filePath].filter(Boolean); // Fallback to first match

    updateResolution(songId, { action: 'some', selectedFiles });
  };

  const handleResolveAll = async () => {
    const resolutionArray = Array.from(resolutions.values()).filter(res => res.action !== 'skip');
    setIsSending(true);
    try {
      await onResolve(resolutionArray);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSuggested = async () => {
    console.log('Send Suggested clicked');
    console.log('Conflicts:', conflicts);
    
    // Build suggested resolutions directly
    const suggestedResolutions = conflicts.map(conflict => {
      // Suggest files with highest confidence, or files with "CONG" or "BULLETIN" in name
      const suggested = conflict.matches.filter(match => 
        match.fileName.toLowerCase().includes('cong') ||
        match.fileName.toLowerCase().includes('bulletin') ||
        match.matchConfidence >= 95
      );

      const selectedFiles = suggested.length > 0 
        ? suggested.map(m => m.filePath)
        : [conflict.matches[0]?.filePath].filter(Boolean); // Fallback to first match

      return {
        songId: conflict.songId,
        songTitle: conflict.songTitle,
        selectedFiles,
        action: selectedFiles.length > 0 ? 'some' as const : 'skip' as const
      };
    }).filter(res => res.action !== 'skip');
    
    console.log('Suggested resolutions:', suggestedResolutions);
    
    setIsSending(true);
    try {
      // Send with the suggested selections
      await onResolve(suggestedResolutions);
    } finally {
      setIsSending(false);
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb > 1 ? `${mb.toFixed(1)}MB` : `${(bytes / 1024).toFixed(0)}KB`;
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext === 'pdf' ? <FileText size={16} /> : <Music size={16} />;
  };

  const getActionBadge = (action: string) => {
    const badges = {
      all: { color: 'success', icon: CheckSquare, text: 'Include All' },
      some: { color: 'warning', icon: Check, text: 'Select Some' },
      skip: { color: 'secondary', icon: Ban, text: 'Skip Song' }
    };
    const badge = badges[action as keyof typeof badges];
    const Icon = badge.icon;
    
    return (
      <span className={`badge bg-${badge.color} d-flex align-items-center`}>
        <Icon size={14} className="me-1" />
        {badge.text}
      </span>
    );
  };

  const totalIncluded = Array.from(resolutions.values()).reduce((total, res) => {
    return total + (res.action === 'skip' ? 0 : res.selectedFiles.length);
  }, 0);

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center">
              <AlertTriangle size={20} className="me-2 text-warning" />
              Resolve File Conflicts
            </h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>

          <div className="modal-body">
            <div className="alert alert-info">
              <strong>Multiple files found for some songs.</strong> Please select which files to include in the email attachment.
              Songs with no selection will be skipped.
            </div>

            <div className="mb-3 d-flex align-items-center justify-content-between">
              <div>
                <strong>{conflicts.length}</strong> songs with conflicts | 
                <strong className="ms-2">{totalIncluded}</strong> files will be included
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => conflicts.forEach(c => handleSelectSuggested(c.songId))}
              >
                Select All Suggested
              </button>
            </div>

            <div className="conflict-list" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {conflicts.map((conflict, index) => {
                const resolution = resolutions.get(conflict.songId);
                if (!resolution) return null;

                return (
                  <div key={`conflict-${conflict.songId}-${index}`} className="card mb-3">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="mb-1">
                            <Music size={18} className="me-2" />
                            {conflict.songTitle}
                          </h6>
                          <small className="text-muted">
                            {conflict.matches.length} files found
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {getActionBadge(resolution.action)}
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleSelectSuggested(conflict.songId)}
                          >
                            Suggest
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card-body">
                      {/* Action Selection */}
                      <div className="row mb-3">
                        <div className="col-12">
                          <div className="btn-group w-100" role="group">
                            <input
                              type="radio"
                              className="btn-check"
                              name={`action_${conflict.songId}`}
                              id={`all_${conflict.songId}`}
                              checked={resolution.action === 'all'}
                              onChange={() => handleActionChange(conflict.songId, 'all')}
                            />
                            <label className="btn btn-outline-success" htmlFor={`all_${conflict.songId}`}>
                              <CheckSquare size={16} className="me-2" />
                              Include All ({conflict.matches.length})
                            </label>

                            <input
                              type="radio"
                              className="btn-check"
                              name={`action_${conflict.songId}`}
                              id={`some_${conflict.songId}`}
                              checked={resolution.action === 'some'}
                              onChange={() => handleActionChange(conflict.songId, 'some')}
                            />
                            <label className="btn btn-outline-warning" htmlFor={`some_${conflict.songId}`}>
                              <Check size={16} className="me-2" />
                              Select Some ({resolution.selectedFiles.length})
                            </label>

                            <input
                              type="radio"
                              className="btn-check"
                              name={`action_${conflict.songId}`}
                              id={`skip_${conflict.songId}`}
                              checked={resolution.action === 'skip'}
                              onChange={() => handleActionChange(conflict.songId, 'skip')}
                            />
                            <label className="btn btn-outline-secondary" htmlFor={`skip_${conflict.songId}`}>
                              <X size={16} className="me-2" />
                              Skip Song
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* File Selection (only show if action is 'some') */}
                      {resolution.action === 'some' && (
                        <div className="file-selection">
                          <h6 className="small text-muted mb-2">Select files to include:</h6>
                          <div className="row">
                            {[...conflict.matches].sort((a, b) => b.matchConfidence - a.matchConfidence).map((match, matchIndex) => {
                              const isSelected = resolution.selectedFiles.includes(match.filePath);
                              
                              return (
                                <div key={`file-${conflict.songId}-${matchIndex}`} className="col-md-6 mb-2">
                                  <div 
                                    className={`card small ${isSelected ? 'border-primary bg-light' : 'border-light'}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleFileToggle(conflict.songId, match.filePath)}
                                  >
                                    <div className="card-body py-2">
                                      <div className="d-flex align-items-center">
                                        <div className="me-2">
                                          {isSelected ? (
                                            <CheckSquare size={16} className="text-primary" />
                                          ) : (
                                            <Square size={16} className="text-muted" />
                                          )}
                                        </div>
                                        <div className="me-2">
                                          {getFileIcon(match.fileName)}
                                        </div>
                                        <div className="flex-grow-1">
                                          <div className="fw-medium">{match.fileName}</div>
                                          <div className="text-muted small">
                                            Confidence: {match.matchConfidence}%
                                            {match.fileSize && ` | ${formatFileSize(match.fileSize)}`}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* File Preview (always show files) */}
                      {resolution.action !== 'some' && (
                        <div className="file-preview">
                          <h6 className="small text-muted mb-2">
                            Files {resolution.action === 'all' ? 'to be included' : 'found'}:
                          </h6>
                          <div className="list-group list-group-flush small">
                            {[...conflict.matches].sort((a, b) => b.matchConfidence - a.matchConfidence).map((match, matchIndex) => (
                              <div key={`preview-${conflict.songId}-${matchIndex}`} className="list-group-item px-0 py-1 d-flex align-items-center">
                                <div className="me-2">{getFileIcon(match.fileName)}</div>
                                <div className="flex-grow-1">
                                  <span className="fw-medium">{match.fileName}</span>
                                  <span className="text-muted ms-2">
                                    ({match.matchConfidence}% match)
                                  </span>
                                </div>
                                {match.fileSize && (
                                  <span className="text-muted small">{formatFileSize(match.fileSize)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <div className="me-auto">
              <small className="text-muted">
                {totalIncluded} files selected from {conflicts.length} songs
              </small>
            </div>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onCancel}
              disabled={isSending}
            >
              <X size={16} className="me-2" />
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-success" 
              onClick={handleSendSuggested}
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Check size={16} className="me-2" />
                  Send Suggested
                </>
              )}
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleResolveAll}
              disabled={totalIncluded === 0 || isSending}
            >
              {isSending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Check size={16} className="me-2" />
                  Continue with Selected Files ({totalIncluded})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileConflictModal;